import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { TRY, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Inbox, FileText, FileCode2, RefreshCw, CheckCircle2, XCircle, Loader2, AlertTriangle, Receipt, ShieldCheck, FlaskConical, History, Building2, Eye, Link2, UserCheck, CalendarDays, Hash, Mail, MapPin, Phone } from "lucide-react";
import { Combobox } from "@/components/Combobox";
import HtmlPreviewDialog, { authUrl, buildIncomingHtml, fetchPreviewUrl } from "@/components/HtmlPreviewDialog";
import QnbIncomingLines from "@/components/QnbIncomingLines";
import ReturnInvoiceDialog from "@/components/ReturnInvoiceDialog";
import QnbInvoiceDialog from "@/components/QnbInvoiceDialog";
import { Undo2 } from "lucide-react";
import { toast } from "sonner";

const PROFILE_LABEL = { TICARIFATURA: "Ticari Fatura", TEMELFATURA: "Temel Fatura", EARSIVFATURA: "e-Arşiv Fatura", TEMELIRSALIYE: "Temel İrsaliye" };
const STATUS = {
  yeni: { label: "Yeni · Yanıt bekliyor", cls: "border-amber-300 bg-amber-50 text-amber-700" },
  kabul: { label: "KABUL edildi", cls: "border-emerald-300 bg-emerald-50 text-emerald-700" },
  red: { label: "RED edildi", cls: "border-red-300 bg-red-50 text-red-600" },
};
export const incomingStatus = (s) => STATUS[s] || STATUS.yeni;
export const profileLabel = (p) => PROFILE_LABEL[(p || "").toUpperCase()] || p || "—";
const fmtDT = (s) => (s ? `${fmtDate(s)}${s.length > 10 ? " " + s.slice(11, 16) : ""}` : "—");

const Info = ({ label, value, mono, icon: Icon }) => (
  <div className="min-w-0">
    <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-1">{Icon && <Icon className="h-3 w-3" />}{label}</div>
    <div className={`text-sm font-medium break-words ${mono ? "font-mono text-xs" : ""}`}>{value || "—"}</div>
  </div>
);
const Section = ({ title, icon: Icon, children, testid, tone = "" }) => (
  <div className={`rounded-2xl border bg-card shadow-sm overflow-hidden ${tone}`} data-testid={testid}>
    <div className="px-3.5 py-2 border-b bg-slate-50/80 text-[11px] font-black uppercase tracking-wider text-secondary flex items-center gap-1.5">{Icon && <Icon className="h-3.5 w-3.5" />}{title}</div>
    <div className="p-3.5 space-y-2.5">{children}</div>
  </div>
);

// Gelen e-Fatura / e-İrsaliye — tam ekran inceleme + KABUL/RED + alış faturasına işleme
export default function QnbIncomingDialog({ doc, onClose, onDone }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null);          // KABUL | RED
  const [note, setNote] = useState("");
  const [addStock, setAddStock] = useState(false);
  const [qnbStatus, setQnbStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [pickContact, setPickContact] = useState(null);
  const [returnDoc, setReturnDoc] = useState(null);     // iade faturası kalem seçimi
  const [returnInv, setReturnInv] = useState(null);     // oluşturulan iade taslağı → QNB kesim diyaloğu
  const [pendingAccept, setPendingAccept] = useState(false);   // tek dokunuş KABUL: 3 sn vazgeçme süresi
  const acceptTimer = useRef(null);
  useEffect(() => () => clearTimeout(acceptTimer.current), []);

  const reload = async (id) => {
    try { const { data } = await api.get(`/qnb/incoming/${id}`); setD(data); } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => {
    setD(null); setMode(null); setNote(""); setAddStock(false); setQnbStatus(null); setPreview(null); setPickContact(null);
    if (doc?.id) { reload(doc.id); api.get("/contacts").then((r) => setContacts(r.data || [])).catch(() => {}); }
  }, [doc]);

  const contactOptions = contacts.map((c) => ({ value: c.id, label: c.title, keywords: `${c.tax_no || ""} ${c.title}`, hint: c.tax_no ? `VKN ${c.tax_no}` : undefined,
    badge: d?.sender_vkn && c.tax_no === d.sender_vkn ? "VKN eşleşti" : undefined, badgeCls: "border-emerald-300 bg-emerald-50 text-emerald-700" }));
  const linkContact = async () => {
    if (!pickContact) return toast.error("Cari seçin");
    setBusy(true);
    try {
      const byId = contacts.some((c) => c.id === pickContact);
      const { data } = await api.put(`/qnb/incoming/${d.id}/contact`, byId ? { contact_id: pickContact } : { contact_title: pickContact });
      toast.success(`Gelen fatura '${data.contact_title}' carisine bağlandı`);
      setPickContact(null); await reload(d.id); onDone && onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const isFatura = d?.doc_type === "FATURA";
  const parsed = d?.parsed || {};
  const sup = parsed.supplier || {};
  const st = incomingStatus(d?.status);
  const answered = d?.status === "kabul" || d?.status === "red";
  const matches = d?.matches || [];
  const matchedCount = matches.filter((m) => m.product_id).length;
  const canEditMatch = isFatura && !d?.invoice_id;

  const setMatch = async (i, productId) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/qnb/incoming/${d.id}/match`, { line_index: i, product_id: productId || null });
      setD({ ...d, matches: data.matches });
      toast.success(productId ? "Kalem stok ürünüyle eşleştirildi" : "Eşleşme kaldırıldı");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const rematch = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/qnb/incoming/${d.id}/match`); setD({ ...d, matches: data.matches }); toast.success("Eşleştirme yeniden hesaplandı"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const open = (url) => window.open(authUrl(url), "_blank");
  const act = async (kind) => {
    setBusy(true);
    try {
      if (kind === "pdf") { const { data } = await api.get(`/qnb/incoming/${d.id}/document?fmt=PDF`); open(data.url); }
      else if (kind === "html") {
        if (d.simulated) setPreview({ html: buildIncomingHtml(d), title: d.belge_no });
        else { const url = await fetchPreviewUrl(`/qnb/incoming/${d.id}/document`); setPreview({ url, title: d.belge_no }); }
      }
      else if (kind === "ubl") { if (d.documents?.ubl) open(d.documents.ubl); else { const { data } = await api.get(`/qnb/incoming/${d.id}/document?fmt=UBL`); open(data.url); } }
      else if (kind === "status") { const { data } = await api.get(`/qnb/incoming/${d.id}/status`); setQnbStatus(data); toast.success("QNB durumu güncellendi"); }
      else if (kind === "invoice") {
        const { data } = await api.post(`/qnb/incoming/${d.id}/to-invoice`, { add_to_stock: addStock });
        toast.success(`Alış faturası oluşturuldu · ${data.invoice.contact_title} carisine borç işlendi`);
        await reload(d.id); onDone && onDone();
      }
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const respond = async (kind) => {
    if (kind === "RED" && !note.trim()) return toast.error("RED için açıklama zorunludur");
    setBusy(true);
    try {
      const { data } = await api.post(`/qnb/incoming/${d.id}/respond`, { response: kind, note, add_to_stock: addStock });
      setD(data); setMode(null);
      if (kind === "KABUL") toast.success(data.created_invoice ? `KABUL edildi · alış faturası oluşturuldu · ${data.created_invoice.contact_title} carisine ${TRY(data.created_invoice.total)} borç işlendi` : "KABUL edildi");
      else toast.success("RED yanıtı gönderildi");
      onDone && onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  // Tek dokunuş KABUL — 3 sn içinde "Vazgeç" ile iptal edilebilir, sonra yanıt gönderilir
  const quickAccept = () => {
    setMode(null); setPendingAccept(true);
    const cancel = () => { clearTimeout(acceptTimer.current); setPendingAccept(false); toast.dismiss(tid); toast("KABUL iptal edildi"); };
    const tid = toast.loading("KABUL yanıtı 3 saniye içinde gönderilecek…", { description: `${d.sender_name} · ${d.belge_no} · ${TRY(d.payable)}`, duration: 3500, action: { label: "Vazgeç", onClick: cancel } });
    acceptTimer.current = setTimeout(() => { toast.dismiss(tid); setPendingAccept(false); respond("KABUL"); }, 3000);
  };
  const isTicari = (d?.profile || "").toUpperCase() === "TICARIFATURA";

  const senderCard = d && (
    <Section title="Gönderen (Tedarikçi)" icon={Building2} testid="incoming-sender">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-secondary text-white font-head font-black flex items-center justify-center shrink-0">{(sup.name || d.sender_name || "?").slice(0, 1)}</div>
        <div className="min-w-0">
          <div className="font-head font-black text-[15px] leading-tight">{sup.name || d.sender_name}</div>
          <div className="text-xs text-muted-foreground font-mono">VKN/TCKN {d.sender_vkn || sup.vkn || "—"}{(sup.tax_office || d.sender_tax_office) ? ` · ${sup.tax_office || d.sender_tax_office}` : ""}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-1.5 text-xs text-muted-foreground">
        {(sup.address || d.sender_address) && <div className="flex gap-1.5"><MapPin className="h-3.5 w-3.5 shrink-0 mt-0.5" />{[sup.address || d.sender_address, sup.district || d.sender_district, sup.city || d.sender_city].filter(Boolean).join(" · ")}</div>}
        {(sup.phone || d.sender_phone) && <div className="flex gap-1.5"><Phone className="h-3.5 w-3.5 shrink-0 mt-0.5" />{sup.phone || d.sender_phone}</div>}
        {(sup.email || d.sender_email) && <div className="flex gap-1.5"><Mail className="h-3.5 w-3.5 shrink-0 mt-0.5" />{sup.email || d.sender_email}</div>}
      </div>
    </Section>
  );

  const docCard = d && (
    <Section title="Belge Bilgileri" icon={FileText} testid="incoming-docinfo">
      <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
        <Info label="Fatura No" value={d.belge_no} mono icon={Hash} />
        <Info label="Senaryo" value={profileLabel(d.profile)} />
        <Info label="Fatura Tipi" value={d.type_code || parsed.type_code} />
        <Info label="Para Birimi" value={d.currency} />
        <Info label="Fatura Tarihi" value={fmtDate(d.belge_tarihi)} icon={CalendarDays} />
        <Info label="Geliş Tarihi" value={fmtDT(d.gelis_tarihi)} />
      </div>
      <Info label="ETTN" value={d.ettn} mono />
      <Info label="GİB Etiketi (PK)" value={d.sender_etiket} mono />
      {(parsed.notes || []).length > 0 && <div className="rounded-lg bg-amber-50 border border-amber-200 px-2.5 py-2 text-xs text-amber-900"><b>Fatura notları:</b> {parsed.notes.join(" · ")}</div>}
    </Section>
  );

  const responseCard = d && (
    <div className={`rounded-2xl border-2 p-3.5 space-y-2 shadow-sm ${d.status === "kabul" ? "border-emerald-300 bg-emerald-50" : d.status === "red" ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`} data-testid="incoming-response">
      <div className="flex items-center gap-2 font-head font-black text-base flex-wrap">
        {d.status === "kabul" ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : d.status === "red" ? <XCircle className="h-5 w-5 text-red-600" /> : <Loader2 className="h-5 w-5 text-amber-600 animate-spin" />}
        {d.status === "kabul" ? "KABUL edildi ✓" : d.status === "red" ? "RED edildi" : "Yanıt bekliyor"}
        <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded-full text-white ${d.status === "kabul" ? "bg-emerald-600" : d.status === "red" ? "bg-red-600" : "bg-amber-500"}`} data-testid="incoming-response-label">{st.label}</span>
      </div>
      {d.response && (
        <div className="text-xs text-muted-foreground space-y-0.5">
          <div>{d.response.response || d.status?.toUpperCase()} · {fmtDT(d.response.sent_at)} · {d.response.sent_by}</div>
          {d.response.doc_id && <div className="font-mono">Yanıt No: {d.response.doc_id}</div>}
          {d.response.note && <div>Açıklama: {d.response.note}</div>}
          {d.response.info && <div className="text-amber-700">{d.response.info}</div>}
          {d.response.result_text && <div>{d.response.result_text}</div>}
        </div>
      )}
      {!d.response && (d.profile || "").toUpperCase() === "TEMELFATURA" && <p className="text-xs text-muted-foreground">Temel fatura: GİB'e yanıt gönderilmez; kabul/red yalnızca sistemde işaretlenir.</p>}
      {!d.response && (d.profile || "").toUpperCase() === "TICARIFATURA" && <p className="text-xs text-muted-foreground">Ticari fatura: 8 gün içinde KABUL veya RED uygulama yanıtı GİB üzerinden gönderilmelidir.</p>}
      {qnbStatus && (
        <div className="text-xs text-muted-foreground border-t pt-2 space-y-0.5" data-testid="incoming-qnb-status">
          {qnbStatus.status?.simulated ? <div>{qnbStatus.status.info}</div> : <>
            <div>QNB Yanıt Durumu: {qnbStatus.status?.yanitDurumu ?? "—"} · Yerele aktarım: {qnbStatus.status?.yereleAktarimDurumu ?? "—"} · GİB iptal: {qnbStatus.status?.gibIptalDurum ?? "—"}</div>
            {(qnbStatus.history || []).length > 0 && <div className="flex items-center gap-1 font-semibold pt-1"><History className="h-3 w-3" />Tarihçe</div>}
            {(qnbStatus.history || []).map((h, i) => <div key={i}>{fmtDT(h.time)} · {h.action}{h.result ? ` · ${h.result}` : ""}</div>)}
          </>}
        </div>
      )}
    </div>
  );

  const contactCard = d && (
    <Section title="Alış Faturası & Cari" icon={Receipt} testid="incoming-invoice-link">
      <div className="rounded-xl border bg-muted/30 p-2.5 space-y-2" data-testid="incoming-contact-block">
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <UserCheck className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-muted-foreground">Bağlı cari:</span>
          <b data-testid="incoming-contact-title">{d.contact_title || "—"}</b>
          {d.contact ? <Badge variant="outline" className="text-[10px] border-emerald-300 bg-emerald-50 text-emerald-700">{d.contact_match === "manual" ? "elle eşlendi" : d.contact_match === "vkn" ? "VKN ile eşleşti" : "mevcut"}</Badge>
            : <Badge variant="outline" className="text-[10px] border-amber-300 bg-amber-50 text-amber-800">yeni açılacak</Badge>}
          {(d.vkn_contacts || []).length > 0 && !(d.vkn_contacts || []).some((c) => c.title === d.contact_title) && (
            <span className="text-[11px] text-amber-800" data-testid="incoming-vkn-hint">Aynı VKN'li mevcut cari: {(d.vkn_contacts || []).map((c) => c.title).join(", ")}</span>
          )}
        </div>
        {!d.invoice_id && (
          <div className="flex gap-2 items-center">
            <Combobox options={contactOptions} value={pickContact} onChange={setPickContact} allowCustom placeholder="Mevcut cariyle eşleştir (ünvan / VKN ara)" searchPlaceholder="Cari ara…" className="h-9 text-xs bg-white flex-1" data-testid="incoming-contact-select" />
            <Button size="sm" variant="outline" className="h-9 shrink-0" onClick={linkContact} disabled={busy || !pickContact} data-testid="incoming-contact-save"><Link2 className="h-3.5 w-3.5 mr-1" />Bağla</Button>
          </div>
        )}
      </div>
      {d.invoice_id ? (
        <div className="flex items-start gap-2 text-sm text-emerald-700"><CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
          <div>Alış faturası oluşturuldu ve <b>{d.contact_title}</b> carisine borç olarak işlendi.
            <div><Link to="/alis" className="underline text-xs" data-testid="incoming-goto-alis">Alış Faturaları → Kayıtlı Faturalar</Link></div>
          </div>
        </div>
      ) : d.invoice_error ? (
        <div className="text-xs text-red-600 flex gap-1"><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />{d.invoice_error}</div>
      ) : (
        <p className="text-xs text-muted-foreground">KABUL edilince otomatik olarak alış faturası oluşturulur ve <b>{d.contact_title || "tedarikçi"}</b> carisine borç işlenir. Farklı bir cariye işlenmesi için yukarıdan eşleştirin.</p>
      )}
      {isFatura && !d.invoice_id && d.status !== "red" && (
        <Button size="sm" variant="outline" className="w-full" onClick={() => act("invoice")} disabled={busy} data-testid="incoming-to-invoice-btn"><Receipt className="h-4 w-4 mr-1.5" />Alış Faturasına İşle (yanıt göndermeden)</Button>
      )}
      {isFatura && (
        <div className="rounded-xl border border-orange-200 bg-orange-50/70 p-2.5 space-y-1.5" data-testid="incoming-return-block">
          <div className="text-[11px] text-orange-950">Yanlış / hasarlı ürün geldiyse bu faturadaki kalemleri seçerek <b>{d.sender_name}</b> firmasına <b>İade Faturası</b> kesebilirsiniz (önizleme ile).</div>
          <Button size="sm" className="w-full bg-orange-600 hover:bg-orange-700" onClick={() => setReturnDoc(d)} disabled={busy || !(parsed.lines || []).length} data-testid="incoming-return-btn"><Undo2 className="h-4 w-4 mr-1.5" />İade Faturası Kes</Button>
          {(d.return_invoice_ids || []).length > 0 && <div className="text-[11px] text-orange-800" data-testid="incoming-return-count">{d.return_invoice_ids.length} iade faturası taslağı/kesimi bu faturaya bağlı · <Link to="/efatura" className="underline">Fatura Kes</Link> / <Link to="/satis" className="underline">Satış Faturaları</Link></div>}
        </div>
      )}
    </Section>
  );

  const decisionCard = d && isFatura && !answered && (
    <div className="rounded-2xl border-2 border-secondary/25 bg-card shadow-md overflow-hidden" data-testid="incoming-decision">
      <div className="px-3.5 py-2 bg-secondary text-white text-[11px] font-black uppercase tracking-wider flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" />Karar · tek dokunuş</div>
      <div className="p-3.5 space-y-3">
        <div className="grid grid-cols-2 gap-2.5">
          <Button className="h-16 flex-col gap-0.5 bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-600/25" onClick={quickAccept} disabled={busy || pendingAccept} data-testid="incoming-accept-btn">
            <span className="flex items-center gap-2 text-lg font-black">{pendingAccept ? <Loader2 className="h-5 w-5 animate-spin" /> : <CheckCircle2 className="h-5 w-5" />}KABUL</span>
            <span className="text-[10px] font-medium opacity-90">{pendingAccept ? "gönderiliyor · vazgeçebilirsiniz" : isTicari ? "GİB'e yanıt gider" : "sistemde işaretlenir"}</span>
          </Button>
          <Button variant="outline" className={`h-16 flex-col gap-0.5 border-2 border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800 ${mode === "RED" ? "bg-red-50 ring-2 ring-red-300" : "bg-card"}`} onClick={() => setMode(mode === "RED" ? null : "RED")} disabled={busy || pendingAccept} data-testid="incoming-reject-btn">
            <span className="flex items-center gap-2 text-lg font-black"><XCircle className="h-5 w-5" />RED</span>
            <span className="text-[10px] font-medium opacity-80">gerekçe yazılır</span>
          </Button>
        </div>
        <label className="flex items-start gap-2 text-xs cursor-pointer">
          <Switch checked={addStock} onCheckedChange={setAddStock} data-testid="incoming-add-stock" />
          <span>Kabulde kalemleri <b>stoğa da ekle</b> (miktar + maliyet güncellenir){matches.length > 0 && <span className="text-muted-foreground" data-testid="incoming-stock-hint"> · {matchedCount}/{matches.length} kalem stokla eşleşti{matchedCount < matches.length ? `, ${matches.length - matchedCount} yeni ürün açılır` : ""}</span>}</span>
        </label>
        {mode === "RED" && (
          <div className="rounded-xl border-2 border-red-300 bg-red-50 p-3 space-y-2" data-testid="incoming-respond-form">
            <div className="text-xs font-bold text-red-800">RED gerekçesi (zorunlu) — GİB'e yanıt olarak iletilir, alış faturası oluşmaz</div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="ör. Fiyatlar anlaşmadan farklı / ürün teslim edilmedi" rows={2} className="bg-white" data-testid="incoming-respond-note" />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setMode(null)} disabled={busy}>Vazgeç</Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => respond("RED")} disabled={busy || !note.trim()} data-testid="incoming-respond-confirm">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <XCircle className="h-4 w-4 mr-1.5" />}RED yanıtını gönder</Button>
            </div>
          </div>
        )}
        <p className="text-[11px] text-muted-foreground leading-snug">
          {isTicari ? "Ticari fatura: 8 gün içinde KABUL / RED uygulama yanıtı GİB'e iletilmelidir." : "Temel fatura: GİB'e yanıt gitmez; karar yalnızca sistemde işaretlenir."} KABUL edilince alış faturası otomatik oluşur ve <b>{d.contact_title || "tedarikçi"}</b> carisine borç işlenir.
        </p>
      </div>
    </div>
  );

  const Chip = ({ label, value, mono, testid }) => (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 border border-white/10 px-2.5 py-0.5 text-[11px] whitespace-nowrap" data-testid={testid}>
      <span className="text-white/55 uppercase tracking-wider text-[9px] font-bold">{label}</span><span className={`font-semibold text-white ${mono ? "font-mono" : ""}`}>{value || "—"}</span>
    </span>
  );

  return (
    <Dialog open={!!doc} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="w-screen h-[100dvh] max-w-none sm:max-w-none max-h-none rounded-none border-0 p-0 gap-0 flex flex-col overflow-hidden left-0 top-0 translate-x-0 translate-y-0 bg-slate-100" data-testid="qnb-incoming-dialog">
        <DialogHeader className="relative px-4 sm:px-5 py-3 bg-[#0B192C] text-white shrink-0 overflow-hidden">
          <span className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full bg-primary/25 blur-3xl" />
          <span className="pointer-events-none absolute left-0 right-0 bottom-0 h-px bg-gradient-to-r from-primary via-orange-300 to-transparent" />
          <DialogTitle asChild>
            <div className="relative flex items-start gap-3 sm:gap-4 pr-12" data-testid="incoming-header">
              <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-2xl bg-primary text-white font-head font-black text-xl flex items-center justify-center shrink-0 shadow-lg shadow-primary/30">{(d?.sender_name || sup.name || "?").trim().slice(0, 1).toLocaleUpperCase("tr")}</div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-[#ffb27a] font-bold font-head"><Inbox className="h-3.5 w-3.5" />Gelen {isFatura || !d ? "e-Fatura" : "e-İrsaliye"} · İncele &amp; Yanıtla{d?.simulated && <Badge className="ml-1 bg-amber-500 text-white border-0 normal-case tracking-normal" data-testid="incoming-simulated-badge"><FlaskConical className="h-3 w-3 mr-1" />TEST simülasyonu</Badge>}</div>
                <div className="font-head font-black text-lg sm:text-2xl leading-tight truncate mt-0.5" data-testid="incoming-sender-title">{d?.sender_name || "Yükleniyor…"}</div>
                {d && (
                  <div className="flex flex-wrap gap-1.5 mt-2" data-testid="incoming-header-chips">
                    <Chip label="Fatura No" value={d.belge_no} mono testid="incoming-chip-no" />
                    <Chip label="Tarih" value={fmtDate(d.belge_tarihi)} testid="incoming-chip-date" />
                    <Chip label="Senaryo" value={profileLabel(d.profile)} testid="incoming-chip-profile" />
                    {(d.type_code || parsed.type_code) && <Chip label="Tip" value={d.type_code || parsed.type_code} />}
                    <Chip label="VKN" value={d.sender_vkn || sup.vkn} mono />
                    <span className="hidden sm:inline"><Chip label="Kalem" value={String((parsed.lines || []).length)} /></span>
                  </div>
                )}
              </div>
              {d && (
                <div className="text-right shrink-0 hidden sm:block">
                  <div className="text-[10px] uppercase tracking-wider text-white/55 font-bold">Ödenecek Tutar</div>
                  <div className="font-head font-black text-2xl lg:text-3xl text-[#ffb27a] tabular-nums leading-none mt-1" data-testid="incoming-header-payable">{TRY(d.payable)}</div>
                  <Badge variant="outline" className={`mt-2 ${st.cls}`} data-testid="incoming-status-badge">{st.label}</Badge>
                </div>
              )}
            </div>
          </DialogTitle>
          {d && <div className="sm:hidden flex items-center justify-between mt-2 relative"><span className="font-head font-black text-xl text-[#ffb27a] tabular-nums">{TRY(d.payable)}</span><Badge variant="outline" className={st.cls}>{st.label}</Badge></div>}
        </DialogHeader>

        {!d ? <div className="p-10 flex justify-center flex-1"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div> : (
          <div className="flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-[minmax(0,1.7fr)_minmax(360px,1fr)] gap-3 p-3 overflow-y-auto xl:overflow-hidden" data-testid="incoming-body">
            <QnbIncomingLines d={d} isFatura={isFatura} matches={matches} canEditMatch={canEditMatch} busy={busy} onSetMatch={setMatch} onRematch={rematch} />
            <div className="space-y-3 pr-0.5 xl:min-h-0 xl:overflow-y-auto" data-testid="incoming-side">
              {decisionCard}
              {(answered || qnbStatus || d.response) && responseCard}
              {contactCard}
              {senderCard}
              {docCard}
              <div className="grid grid-cols-4 gap-2" data-testid="incoming-doc-actions">
                <Button variant="outline" size="sm" className="bg-card" onClick={() => act("status")} disabled={busy} data-testid="incoming-status-btn"><RefreshCw className="h-4 w-4 mr-1.5" />Durum</Button>
                <Button variant="outline" size="sm" className="bg-card" onClick={() => act("html")} disabled={busy} data-testid="incoming-html-btn"><Eye className="h-4 w-4 mr-1.5" />Önizle</Button>
                <Button variant="outline" size="sm" className="bg-card" onClick={() => act("pdf")} disabled={busy || d.simulated} title={d.simulated ? "TEST simülasyonunda PDF yok" : "PDF İndir"} data-testid="incoming-pdf-btn"><FileText className="h-4 w-4 mr-1.5" />PDF</Button>
                <Button variant="outline" size="sm" className="bg-card" onClick={() => act("ubl")} disabled={busy} data-testid="incoming-ubl-btn"><FileCode2 className="h-4 w-4 mr-1.5" />UBL</Button>
              </div>
            </div>
          </div>
        )}
        <DialogFooter className="px-3 py-2.5 border-t gap-2 shrink-0 bg-card flex-row items-center">
          <Button variant="outline" onClick={() => onClose && onClose()} data-testid="incoming-close">Kapat</Button>
          <div className="flex-1 text-xs text-muted-foreground hidden sm:block">{d && isFatura && !answered ? "Karar için sağ üstteki KABUL / RED düğmelerini kullanın." : ""}</div>
          {d && answered && (
            <span className={`inline-flex items-center h-10 px-4 rounded-md text-white text-sm font-bold select-none ${d.status === "kabul" ? "bg-emerald-600" : "bg-red-600"}`} data-testid="incoming-answered-badge">
              {d.status === "kabul" ? <CheckCircle2 className="h-4 w-4 mr-1.5" /> : <XCircle className="h-4 w-4 mr-1.5" />}{d.status === "kabul" ? "KABUL edildi ✓" : "RED edildi"}
            </span>
          )}
        </DialogFooter>
        <HtmlPreviewDialog open={!!preview} title={preview?.title} url={preview?.url} html={preview?.html} onClose={() => setPreview(null)} />
        <ReturnInvoiceDialog doc={returnDoc} onClose={() => setReturnDoc(null)} onCreated={(inv) => { setReturnDoc(null); setReturnInv(inv); reload(d.id); onDone && onDone(); }} />
        <QnbInvoiceDialog invoice={returnInv} contacts={contacts} onClose={() => setReturnInv(null)} onDone={() => { onDone && onDone(); }} />
      </DialogContent>
    </Dialog>
  );
}
